/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903853583 — Знижки */

import { useTranslation } from 'react-i18next';
import styles from './CampaignBlock.module.css';

interface CampaignBlockProps {
    /** Extra class for the outer block — lets a host constrain its width. */
    className?: string;
    /** Already-resolved currency label (e.g. "₴"). */
    currencyLabel: string;
    /** Pre-discount price (currency units) — rendered struck-through. */
    originalAmount: number;
    /** Pre-computed `originalAmount − discountedAmount`. */
    savings?: number;
    /** Discount % — drives the badge label. */
    discountPercentage?: number | null;
    /** Custom campaign message; replaces the "Happy Hours" label during the window. */
    campaignMessage?: string | null;
    /** Happy Hours window; yellow variant when current local time falls inside. */
    startHour?: number | null;
    endHour?: number | null;
    isBadgeVisible?: boolean;
}

const hourToMinutes = (hour: number | null | undefined): number | null => {
    if (typeof hour !== 'number' || isNaN(hour)) return null;
    return hour * 60;
};

const isWithinHappyHoursWindow = (
    start: number | null | undefined,
    end: number | null | undefined,
): boolean => {
    const startMin = hourToMinutes(start);
    const endMin = hourToMinutes(end);
    if (startMin === null || endMin === null) return false;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    if (startMin > endMin) return nowMin >= startMin || nowMin < endMin;
    return startMin <= nowMin && nowMin < endMin;
};

export const CampaignBlock = ({
    className,
    currencyLabel,
    originalAmount,
    savings,
    discountPercentage,
    campaignMessage,
    startHour,
    endHour,
    isBadgeVisible = true,
}: CampaignBlockProps) => {
    const { t } = useTranslation();
    const isHappyHours = isWithinHappyHoursWindow(startHour, endHour);

    return (
        <div
            className={`${styles.campaignBlock}${isHappyHours ? ` ${styles.happyHours}` : ''}${className ? ` ${className}` : ''}`}
        >
            <div className={styles.badgeRow}>
                {isHappyHours && <span className={styles.badge}>
                    {campaignMessage || t('campaign.happyHours')}&nbsp;−{discountPercentage}%
                </span>}

                {!isHappyHours && isBadgeVisible && <span className={styles.badge}>
                    −{discountPercentage}%
                </span>}

                {!isHappyHours && (
                    <span className={styles.savings}>
                        {t('campaign.savings', {
                            amount: (savings ?? 0).toFixed(2),
                            currency: currencyLabel,
                        })}
                    </span>
                )}
            </div>

            <span className={styles.originalPrice}>
                {originalAmount.toFixed(2)} {currencyLabel}
            </span>

            {/* Check type of both hours because they may be 0 (midnight) */}
            {isHappyHours
                && typeof startHour === 'number'
                && typeof endHour === 'number' && (
                <span className={styles.untilTime}>
                    {t('campaign.timeRange', {
                        from: `${startHour}`,
                        until: `${endHour}`,
                    })}
                </span>
            )}

            {!isHappyHours && campaignMessage && (
                <span className={styles.campaignMessage}>
                    {campaignMessage}
                </span>
            )}
        </div>
    );
};
